/** @jsxImportSource @emotion/react */
import React, { FC, useEffect, useState } from 'react';
import { Provider, useSelector } from 'react-redux';

import { IntlProvider } from 'react-intl';
import {
    InvalidConfigError,
    MessageFormatError,
    MissingDataError,
    MissingTranslationError,
    UnsupportedFormatterError,
} from '@formatjs/intl/src/error';
import { FormatError } from 'intl-messageformat';
import { DefaultToastOptions, Toaster } from 'react-hot-toast';
import { Z_INDEX } from '../constants/zIndex';

import locales from '../assets/i18n';
import { store } from '../store/store';
import { selectAppState } from '../store/app/app.selector';

import appStyles from './app.styles';
import MenuBlock from '../components/menu-block/menu-block';
import Routing from './routing/routing';
import LeafletMap from '../components/leaflet-map/leaflet-map.component';
import { MapProvider } from '../components/leaflet-map/MapContext';
import { useGeoLocation } from '../hooks/useGeoLocation';

const logIntlError = (
    message:
        | MissingTranslationError
        | MessageFormatError
        | MissingDataError
        | InvalidConfigError
        | UnsupportedFormatterError
        | FormatError
): void => {
    console.warn(message);
};

const toastOptions: DefaultToastOptions = {
    duration: 1200,
    position: 'top-center',
    style: {
        zIndex: Z_INDEX.TOAST,
    },
};

const toastContainerStyles = {
    zIndex: Z_INDEX.TOAST,
};

const App: FC = () => {
    const { locale } = useSelector(selectAppState);
    const { fetchUserGeolocation } = useGeoLocation();
    const [mounted, setMounted] = useState(false);
    const messages = {
        ...locales.ru,
        ...locales[locale],
    };

    useEffect(() => {
        if (!mounted) {
            setMounted(true);
            return;
        }
        fetchUserGeolocation();
    }, [mounted]);

    return (
        <>
            <IntlProvider
                defaultLocale={'en'}
                locale={locale === 'ua' ? 'uk-UA' : locale}
                messages={messages}
                onError={logIntlError}>
                <Toaster toastOptions={toastOptions} containerStyle={toastContainerStyles} />
                <div css={appStyles.root}>
                    <MapProvider>
                        <MenuBlock />
                        <Routing />
                        <LeafletMap />
                    </MapProvider>
                </div>
            </IntlProvider>
        </>
    );
};

const ConnectedApp: FC = () => (
    <Provider store={store}>
        <App />
    </Provider>
);

export default ConnectedApp;
